import {
  Button,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  cn,
} from '@sho-bot/ui';
import { IconInfoCircle } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * One labelled number, with its definition progressively disclosed.
 *
 * The definition is not decoration. "Stale spans" and "unsettled spans" are both
 * plausible readings of the same words, and an operator making a decision from a
 * count needs to know which one they are looking at without going to read the
 * source.
 *
 * Activating the labelled info button opens the full definition in a popover
 * that also works with a keyboard or touch screen. Facts without a definition
 * still use this layout so run details and health numbers share one scale.
 */
export function Metric({
  label,
  value,
  definition,
  hint,
}: {
  label: string;
  value: ReactNode;
  definition?: string;
  hint?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <span>{label}</span>
        {definition === undefined ? null : (
          <Popover>
            <PopoverTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-3xs"
                  className="text-muted-foreground"
                  aria-label={`About ${label}`}
                />
              }
            >
              <IconInfoCircle className="size-3.5" aria-hidden="true" stroke={1.5} />
            </PopoverTrigger>
            <PopoverContent align="start" size="sm">
              <PopoverHeader>
                <PopoverTitle>{label}</PopoverTitle>
                <PopoverDescription>{definition}</PopoverDescription>
              </PopoverHeader>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="tabular text-lg leading-tight font-medium">{value}</div>
      {hint === undefined ? null : <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function MetricGrid({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={cn('grid gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-4', className)}>
      {children}
    </div>
  );
}
